/**
 * Menu - Main Menu UI
 * 
 * Casino/Vampire Survivors inspired satisfying UI.
 * Features hover effects, animations, and audio feedback.
 */

import { Difficulty } from '../game/StateManager';

/** Menu callback type */
export type MenuSelectCallback = (difficulty: Difficulty) => void;

/** Difficulty button config */
interface DifficultyButton {
    key: Difficulty;
    label: string;
    color: string;
    hoverColor: string;
}

const DIFFICULTIES: DifficultyButton[] = [
    { key: 'easy', label: 'SLOW', color: '#4CAF50', hoverColor: '#66BB6A' },
    { key: 'normal', label: 'NORMAL', color: '#2196F3', hoverColor: '#42A5F5' },
    { key: 'hard', label: 'FAST', color: '#f44336', hoverColor: '#EF5350' },
];

/**
 * Menu class - main menu overlay
 */
export class Menu {
    private container: HTMLDivElement;
    private onSelect?: MenuSelectCallback;
    private buttons: HTMLButtonElement[] = [];
    
    // Audio callback (to be wired up)
    private onHover?: () => void;
    private onClick?: () => void;

    constructor(parentElement: HTMLElement) {
        this.container = document.createElement('div');
        this.container.id = 'game-menu';
        this.container.innerHTML = this.getHTML();
        parentElement.appendChild(this.container);

        this.applyStyles();
        this.setupInteractions();
    }

    /**
     * Set callback for difficulty selection
     */
    setOnSelect(callback: MenuSelectCallback): void {
        this.onSelect = callback;
    }

    /**
     * Set audio callbacks
     */
    setAudioCallbacks(onHover: (() => void) | undefined, onClick: (() => void) | undefined): void {
        if (onHover !== undefined) {
            this.onHover = onHover;
        }
        if (onClick !== undefined) {
            this.onClick = onClick;
        }
    }

    /**
     * Get menu HTML
     */
    private getHTML(): string {
        const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        const controlsText = isTouchDevice 
            ? 'Drag to Move • Arc Motion to Rotate' 
            : 'Drag to Move • A/D to Rotate';

        const buttonsHTML = DIFFICULTIES.map(d => `
            <button class="menu-btn" data-difficulty="${d.key}" style="--btn-color: ${d.color}; --btn-hover: ${d.hoverColor}">
                <span class="menu-btn-text">${d.label}</span>
                <span class="menu-btn-glow"></span>
            </button>
        `).join('');

        return `
            <div class="menu-background">
                <div class="menu-particles"></div>
            </div>
            <div class="menu-content">
                <div class="menu-title">
                    <span class="title-t">T</span><span class="title-dash">-</span><span class="title-stacker">STACKER</span>
                </div>
                <div class="menu-subtitle">Clock out. Go to sort.</div>
                <div class="menu-buttons">
                    ${buttonsHTML}
                </div>
                <div class="menu-controls">${controlsText}</div>
            </div>
        `;
    }

    /**
     * Apply CSS styles
     */
    private applyStyles(): void {
        const style = document.createElement('style');
        style.id = 'menu-styles';
        style.textContent = `
            #game-menu {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                z-index: 200;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            }

            .menu-background {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f0f1a 100%);
            }

            .menu-particles {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background-image: 
                    radial-gradient(2px 2px at 20px 30px, rgba(255,255,255,0.3), transparent),
                    radial-gradient(2px 2px at 40px 70px, rgba(255,255,255,0.2), transparent),
                    radial-gradient(1px 1px at 90px 40px, rgba(255,255,255,0.4), transparent),
                    radial-gradient(2px 2px at 130px 80px, rgba(255,255,255,0.2), transparent),
                    radial-gradient(1px 1px at 160px 120px, rgba(255,255,255,0.3), transparent);
                background-size: 200px 150px;
                animation: twinkle 4s ease-in-out infinite;
            }

            @keyframes twinkle {
                0%, 100% { opacity: 0.5; }
                50% { opacity: 1; }
            }

            .menu-content {
                position: relative;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100%;
                padding: 20px;
            }

            .menu-title {
                font-size: clamp(48px, 12vw, 72px);
                font-weight: bold;
                margin-bottom: 10px;
                animation: float 3s ease-in-out infinite;
            }

            @keyframes float {
                0%, 100% { transform: translateY(0); }
                50% { transform: translateY(-10px); }
            }

            .title-t {
                color: #9b59b6;
                text-shadow: 0 0 20px rgba(155, 89, 182, 0.5);
            }

            .title-dash {
                color: #e91e63;
                text-shadow: 0 0 20px rgba(233, 30, 99, 0.5);
            }

            .title-stacker {
                color: #00bcd4;
                text-shadow: 0 0 20px rgba(0, 188, 212, 0.5);
            }

            .menu-subtitle {
                font-size: 18px;
                color: #888;
                margin-bottom: 50px;
                letter-spacing: 4px;
                text-transform: uppercase;
            }

            .menu-buttons {
                display: flex;
                flex-direction: column;
                gap: 15px;
                margin-bottom: 50px;
            }

            .menu-btn {
                position: relative;
                padding: 18px 60px;
                font-size: 22px;
                font-weight: bold;
                font-family: inherit;
                color: white;
                background: var(--btn-color);
                border: none;
                border-radius: 8px;
                cursor: pointer;
                overflow: hidden;
                transition: transform 0.15s ease, box-shadow 0.15s ease;
                box-shadow: 0 4px 15px rgba(0,0,0,0.3);
            }

            .menu-btn:hover {
                transform: scale(1.05);
                box-shadow: 0 6px 25px rgba(0,0,0,0.4), 0 0 30px color-mix(in srgb, var(--btn-color) 50%, transparent);
            }

            .menu-btn:active {
                transform: scale(0.98);
            }

            .menu-btn-text {
                position: relative;
                z-index: 1;
            }

            .menu-btn-glow {
                position: absolute;
                top: 0;
                left: -100%;
                width: 100%;
                height: 100%;
                background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
                transition: left 0.5s ease;
            }

            .menu-btn:hover .menu-btn-glow {
                left: 100%;
            }

            .menu-controls {
                font-size: 14px;
                color: #666;
                letter-spacing: 1px;
            }

            /* Hide when not visible */
            #game-menu.hidden {
                display: none;
            }

            /* Fade in animation */
            #game-menu.fade-in {
                animation: fadeIn 0.3s ease-out;
            }

            @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
            }

            /* Fade out animation */
            #game-menu.fade-out {
                animation: fadeOut 0.3s ease-out forwards;
            }

            @keyframes fadeOut {
                from { opacity: 1; }
                to { opacity: 0; }
            }
        `;
        document.head.appendChild(style);
    }

    /**
     * Setup button interactions
     */
    private setupInteractions(): void {
        this.buttons = Array.from(this.container.querySelectorAll('.menu-btn'));

        for (const btn of this.buttons) {
            const difficulty = btn.dataset['difficulty'] as Difficulty;

            // Hover effect
            btn.addEventListener('mouseenter', () => {
                this.onHover?.();
            });

            // Click effect
            btn.addEventListener('click', () => {
                this.onClick?.();
                this.selectDifficulty(difficulty);
            });

            // Touch feedback
            btn.addEventListener('touchstart', () => {
                btn.style.transform = 'scale(0.98)';
            });

            btn.addEventListener('touchend', () => {
                btn.style.transform = '';
            });
        }
    }

    /**
     * Handle difficulty selection
     */
    private selectDifficulty(difficulty: Difficulty): void {
        // Animate out
        this.container.classList.add('fade-out');

        setTimeout(() => {
            this.hide();
            this.onSelect?.(difficulty);
        }, 300);
    }

    /**
     * Show the menu
     */
    show(): void {
        this.container.classList.remove('hidden', 'fade-out');
        this.container.classList.add('fade-in');
    }

    /**
     * Hide the menu
     */
    hide(): void {
        this.container.classList.add('hidden');
        this.container.classList.remove('fade-in', 'fade-out');
    }

    /**
     * Check if menu is visible
     */
    isVisible(): boolean {
        return !this.container.classList.contains('hidden');
    }

    /**
     * Clean up
     */
    destroy(): void {
        this.container.remove();
        document.getElementById('menu-styles')?.remove();
    }
}
